import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    google: any;
    initGoogleMaps: () => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService {
  private isLoaded = false;
  private isLoading = false;
  private loadPromise: Promise<void> | null = null;
  private loadReject: ((reason?: any) => void) | null = null;

  constructor() {
    this.autoLoadGoogleMaps();
  }

  private autoLoadGoogleMaps(): void {
    if (!environment.googleMapKey) {
      return;
    }

    if (window.google?.maps) {
      this.isLoaded = true;
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      this.setupExistingScript(existingScript as HTMLScriptElement);
      return;
    }

    this.createAndLoadScript();
  }

  private setupExistingScript(script: HTMLScriptElement): void {
    script.addEventListener('load', () => {
      this.isLoaded = true;
      this.isLoading = false;
    });
    script.addEventListener('error', () => {
      this.isLoading = false;
      this.rejectLoadPromise(new Error('Failed to load existing Google Maps script'));
    });
  }

  private createAndLoadScript(): void {
    this.isLoading = true;
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      this.isLoaded = true;
      this.isLoading = false;
    };

    script.onerror = () => {
      this.isLoading = false;
      this.rejectLoadPromise(new Error('Failed to load Google Maps API script'));
    };

    document.head.appendChild(script);
  }

  private rejectLoadPromise(error: Error): void {
    if (this.loadReject) {
      this.loadReject(error);
      this.loadReject = null;
    }
  }

  waitForGoogleMaps(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    if (this.isLoading && this.loadPromise !== null) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      this.loadReject = reject;
      
      const checkLoaded = () => {
        if (this.isLoaded) {
          resolve();
        } else if (!this.isLoading) {
          reject(new Error('Google Maps failed to load'));
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
    });

    return this.loadPromise;
  }

  isGoogleMapsLoaded(): boolean {
    return this.isLoaded && window.google && window.google.maps;
  }

  getGoogleMaps(): any {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API is not loaded yet');
    }
    return window.google.maps;
  }

  createAutocomplete(input: HTMLInputElement, options?: any): any {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API is not loaded yet');
    }
    
    return new window.google.maps.places.Autocomplete(input, options);
  }

  createPlacesService(map?: any): any {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps API is not loaded yet');
    }
    
    return new window.google.maps.places.PlacesService(map || document.createElement('div'));
  }
}
